import React, { useState } from 'react';
import { Truck, AlertTriangle, ShieldAlert, AlertCircle, Info, Users, Tag, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import { PropostaSaldoInfo, PropostaItemSaldo } from '../../services/propostaSaldoService';

interface PropostaSaldoBannerProps {
  saldo: PropostaSaldoInfo | null;
  loading?: boolean;
  bloqueioAtivo?: boolean;
  selectedItemServicoId?: string | null;
  selectedItemAjudanteId?: string | null;
}

export const PropostaSaldoBanner: React.FC<PropostaSaldoBannerProps> = ({
  saldo,
  loading = false,
  bloqueioAtivo = false,
  selectedItemServicoId,
  selectedItemAjudanteId,
}) => {
  const [showAllItems, setShowAllItems] = useState(false);

  if (loading) {
    return (
      <div className="mt-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-2 text-xs text-slate-500 animate-pulse">
        <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
        <span>Calculando saldo individual dos itens da proposta...</span>
      </div>
    );
  }

  if (!saldo) return null;

  const { totalContratado, totalUtilizado, saldoRestante, percentualUtilizado, statusSaldo, pedidoId, itens = [] } = saldo;

  // Encontra os itens atualmente selecionados no formulário
  const selectedServicoItem = selectedItemServicoId
    ? itens.find((it) => String(it.id) === String(selectedItemServicoId))
    : undefined;

  const selectedAjudanteItem = selectedItemAjudanteId
    ? itens.find((it) => String(it.id) === String(selectedItemAjudanteId))
    : undefined;

  // Verifica se o item específico selecionado está esgotado
  const isSelectedServicoEsgotado = selectedServicoItem && selectedServicoItem.quantidadeContratada > 0 && selectedServicoItem.saldoRestante <= 0;
  const isSelectedAjudanteEsgotado = selectedAjudanteItem && selectedAjudanteItem.quantidadeContratada > 0 && selectedAjudanteItem.saldoRestante <= 0;

  // Sem cota de itens definida
  if (statusSaldo === 'sem_cota' && itens.length === 0) {
    return (
      <div className="mt-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-2 text-xs text-slate-600">
        <Info className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <div>
          <span className="font-semibold text-slate-700">Proposta #{pedidoId}:</span>
          <span className="ml-1 text-slate-500">Contrato sob demanda sem cota fixa pré-definida ({totalUtilizado} OSs emitidas).</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2.5 space-y-2">
      {/* Alerta Geral da Proposta (10, 5, 1, 0) */}
      {saldo.totalContratado > 0 && saldo.nivelAlertaGeral && saldo.nivelAlertaGeral !== 'normal' && saldo.nivelAlertaGeral !== 'sem_cota' && (
        <div className={`p-3 rounded-lg text-xs shadow-xs border-2 ${
          saldo.nivelAlertaGeral === 'esgotado_0'
            ? 'bg-red-50 border-red-500 text-red-950'
            : saldo.nivelAlertaGeral === 'urgente_1'
            ? 'bg-red-50 border-red-400 text-red-900 animate-pulse'
            : saldo.nivelAlertaGeral === 'critico_5'
            ? 'bg-orange-50 border-orange-400 text-orange-950'
            : 'bg-amber-50 border-amber-400 text-amber-950'
        }`}>
          <div className="flex items-start gap-2.5">
            {saldo.nivelAlertaGeral === 'esgotado_0' ? (
              <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            ) : saldo.nivelAlertaGeral === 'urgente_1' ? (
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${saldo.nivelAlertaGeral === 'critico_5' ? 'text-orange-600' : 'text-amber-600'}`} />
            )}
            <div className="flex-1">
              <div className="font-bold uppercase tracking-wide text-2xs mb-0.5">
                {saldo.alertaTituloGeral}
              </div>
              <p className="font-semibold text-xs leading-snug">
                {saldo.alertaMensagemGeral}
              </p>
              {saldo.alertaBloqueadoGeral && (
                <p className="mt-1 text-2xs font-bold text-red-700">
                  ⛔ Emissões bloqueadas no sistema até renovação ou aditivo contratual.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alerta de Bloqueio do Item Específico Selecionado */}
      {(isSelectedServicoEsgotado || isSelectedAjudanteEsgotado) && (
        <div className={`p-3 rounded-lg text-xs shadow-xs border-2 ${
          bloqueioAtivo ? 'bg-red-50 border-red-400 text-red-900' : 'bg-amber-50 border-amber-400 text-amber-900'
        }`}>
          <div className="flex items-start gap-2.5">
            {bloqueioAtivo ? (
              <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between font-bold uppercase tracking-wide text-2xs mb-0.5">
                <span>{bloqueioAtivo ? '⛔ Bloqueio: Cota do Item Esgotada' : '⚠️ Aviso: Cota do Item Esgotada'}</span>
              </div>
              {isSelectedServicoEsgotado && (
                <p className="font-semibold">
                  O veículo/serviço <strong>"{selectedServicoItem?.servicos}"</strong> consumiu todas as {selectedServicoItem?.quantidadeContratada} diárias/viagens contratadas ({selectedServicoItem?.quantidadeUtilizada} utilizadas, Saldo: {selectedServicoItem?.saldoRestante}).
                </p>
              )}
              {isSelectedAjudanteEsgotado && (
                <p className="font-semibold mt-1">
                  O serviço de ajudante <strong>"{selectedAjudanteItem?.servicos}"</strong> consumiu a cota de {selectedAjudanteItem?.quantidadeContratada} ({selectedAjudanteItem?.quantidadeUtilizada} utilizados, Saldo: {selectedAjudanteItem?.saldoRestante}).
                </p>
              )}
              <p className={`mt-1 text-2xs font-medium ${bloqueioAtivo ? 'text-red-700' : 'text-amber-800'}`}>
                {bloqueioAtivo
                  ? 'A emissão desta OS está bloqueada para itens sem saldo. Escolha outro item com saldo disponível ou solicite um aditivo contratual.'
                  : 'A emissão é permitida em modo flexível, mas certifique-se de alinhar a cobrança adicional com o cliente.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cartão Geral do Saldo da Proposta com Decomposição dos Itens */}
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
        {/* Cabeçalho Resumo */}
        <div className="flex items-center justify-between pb-2 mb-2.5 border-b border-slate-200/80">
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-md bg-red-100 text-red-700">
              <Truck className="w-3.5 h-3.5" />
            </div>
            <div>
              <span className="font-bold text-slate-800 text-xs">
                Saldos por Item da Proposta #{pedidoId}
              </span>
              <span className="text-[11px] text-slate-500 ml-1.5">
                ({totalUtilizado} OSs ativas / {totalContratado} cotas totais)
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowAllItems(!showAllItems)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 cursor-pointer"
          >
            <span>{showAllItems ? 'Recolher' : 'Ver Detalhes dos Itens'}</span>
            {showAllItems ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* Grade de Itens Contratados */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(showAllItems ? itens : itens.slice(0, 4)).map((item) => {
            const isSelected = String(item.id) === String(selectedItemServicoId) || String(item.id) === String(selectedItemAjudanteId);
            const isEsgotado = item.statusSaldo === 'esgotado';
            const isAlerta = item.statusSaldo === 'alerta';

            return (
              <div
                key={item.id}
                className={`p-2 rounded-lg border transition-all text-xs ${
                  isSelected
                    ? 'border-red-500 bg-red-50/50 shadow-xs ring-1 ring-red-400'
                    : isEsgotado
                    ? 'border-red-200 bg-red-50/30'
                    : isAlerta
                    ? 'border-amber-200 bg-amber-50/30'
                    : 'border-slate-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-1.5">
                  <div className="flex items-start gap-1.5 overflow-hidden">
                    {item.categoria === 'ajudante' ? (
                      <Users className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
                    ) : item.categoria === 'veiculo' ? (
                      <Truck className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
                    ) : (
                      <Tag className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" />
                    )}
                    <span className="font-semibold text-slate-800 truncate" title={item.servicos}>
                      {item.servicos}
                    </span>
                  </div>

                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 ${
                      item.nivelAlerta === 'esgotado_0'
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : item.nivelAlerta === 'urgente_1'
                        ? 'bg-red-50 text-red-700 border border-red-300 animate-pulse'
                        : item.nivelAlerta === 'critico_5'
                        ? 'bg-orange-100 text-orange-800 border border-orange-200'
                        : item.nivelAlerta === 'baixo_10'
                        ? 'bg-amber-100 text-amber-800 border border-amber-200'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    {item.nivelAlerta === 'esgotado_0' ? 'Esgotado' : `${item.saldoRestante} Rest.`}
                  </span>
                </div>

                {/* Barra de Progresso e Métricas */}
                <div className="mt-1.5">
                  <div className="w-full bg-slate-200/80 rounded-full h-1 overflow-hidden">
                    <div
                      className={`h-1 rounded-full transition-all duration-300 ${
                        item.nivelAlerta === 'esgotado_0'
                          ? 'bg-red-600'
                          : item.nivelAlerta === 'urgente_1'
                          ? 'bg-red-500'
                          : item.nivelAlerta === 'critico_5'
                          ? 'bg-orange-500'
                          : item.nivelAlerta === 'baixo_10'
                          ? 'bg-amber-500'
                          : 'bg-emerald-600'
                      }`}
                      style={{ width: `${item.percentualUtilizado}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1 font-medium">
                    <span>
                      Usado: <strong>{item.quantidadeUtilizada}</strong> / {item.quantidadeContratada}
                    </span>
                    <span>{item.percentualUtilizado}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {itens.length > 4 && !showAllItems && (
          <div className="text-center mt-2">
            <button
              type="button"
              onClick={() => setShowAllItems(true)}
              className="text-[11px] font-bold text-red-600 hover:text-red-700 underline cursor-pointer"
            >
              + Exibir mais {itens.length - 4} itens contratados nesta proposta
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
